import { freezeFromBaseRow, fromBaseClone } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBaseEntryResult } from './from-base-entry';
import type { FromBaseDefinitionCache } from './from-base-definition-finalization';
import { sameJson } from './json-change';

interface MemberMemo {
    dependencies: string;
    saved: Row;
    preparedReads: Map<string, Row | undefined>;
    servedReads: Map<string, boolean>;
    result: FromBaseEntryResult;
    prepared: Map<string, Row>;
    served: Set<string>;
    failedCatalog?: Row;
}

/** Expiry is decided by the time boundary and changes saved status rows. Clock
 * checkpoints alone cannot change a member; light/sun/cycle and calendar stay in
 * this witness so their actual condition changes still invalidate it. */
const clockCheckpoint = (key: string): boolean => /^(?:campaign_time_(?:year|month|day|hour|minute|second)|campaign_clock_.*|campaign_t_updated|campaign_updated_by_id)$/.test(key);
function sameSaved(previous: Row, member: Row): boolean {
    const keys = Object.keys(member).filter(key => key !== 'catalog');
    const before = Object.keys(previous);
    return keys.length === before.length && keys.every((key, i) => key === before[i]
        && sameJson(previous[key], member[key], key === 'encounter_context' ? clockCheckpoint : undefined));
}

/** Owned by one compiler/runtime, never shared between browser bundles or viewers. */
export function createEncounterMemberMemo(immutableInputs = false) {
    const members = new Map<string, Map<string, MemberMemo>>();
    const counters = { compiles: 0, hits: 0 };
    const perMember = new Map<string, number>();
    function encounter(id: string, roster: readonly string[], dependencies: string, cache: FromBaseDefinitionCache, reuseSaved = false) {
        id = id.toUpperCase();
        const held = members.get(id) ?? new Map<string, MemberMemo>();
        members.set(id, held);
        const ids = new Set(roster.map(id => id.toUpperCase()));
        for (const key of held.keys()) if (!ids.has(key)) held.delete(key);
        // Bound pages that navigate through many encounters without retaining old rosters forever.
        members.delete(id); members.set(id, held);
        while (members.size > 16) members.delete(members.keys().next().value!);
        return (member: Row, compile: (cache: FromBaseDefinitionCache) => FromBaseEntryResult): FromBaseEntryResult => {
            const key = String(member.entity_id).toUpperCase();
            const previous = held.get(key);
            if (previous && previous.dependencies === dependencies && sameSaved(previous.saved, member)
                && (previous.failedCatalog === undefined || sameJson(previous.failedCatalog, member.catalog))
                && (reuseSaved || [...previous.preparedReads].every(([token, before]) => {
                    const now = cache.prepared.get(token);
                    return sameJson(before, now);
                }) && [...previous.servedReads].every(([token, before]) => cache.served.has(token) === before))) {
                counters.hits++;
                for (const [token, row] of previous.prepared) if (!reuseSaved || !cache.prepared.has(token)) cache.prepared.set(token, row);
                for (const token of previous.served) cache.served.add(token);
                return previous.result;
            }
            counters.compiles++;
            perMember.set(key, (perMember.get(key) ?? 0) + 1);
            while (perMember.size > 4096) perMember.delete(perMember.keys().next().value!);
            const preparedReads = new Map<string, Row | undefined>(), servedReads = new Map<string, boolean>();
            const prepared = new Map<string, Row>(), served = new Set<string>();
            // Observe only reads of the incoming cache, before this member writes a token.
            // Frozen definition bytes also matter: an earlier member can rebuild a token
            // with a different pending projection without changing its presence/served bit.
            const observed: FromBaseDefinitionCache = {
                prepared: new class extends Map<string, Row> {
                    override get(token: string) {
                        const row = cache.prepared.get(token);
                        if (!prepared.has(token)) preparedReads.set(token, row);
                        return row;
                    }
                    override set(token: string, row: Row): this {
                        prepared.set(token, row); cache.prepared.set(token, row); return this;
                    }
                }(),
                served: new class extends Set<string> {
                    override has(token: string) {
                        const present = cache.served.has(token);
                        if (!served.has(token)) servedReads.set(token, present);
                        return present;
                    }
                    override add(token: string): this { served.add(token); cache.served.add(token); return this; }
                }(),
            };
            const result = compile(observed);
            const { catalog: _catalog, ...saved } = member;
            held.set(key, {
                dependencies, saved: immutableInputs ? saved : fromBaseClone(saved), preparedReads, servedReads,
                result: freezeFromBaseRow(result),
                // Replay this member's writes only, preserving unrelated earlier tokens.
                prepared, served, ...(result.stagesMissing.length ? { failedCatalog: immutableInputs ? member.catalog as Row : fromBaseClone(member.catalog as Row) } : {}),
            });
            return result;
        };
    }
    return { encounter, clear: () => members.clear(), stats: () => ({ ...counters }), memberCompiles: () => Object.fromEntries(perMember) };
}
